import React, {useState} from 'react';
import {Card, Form} from 'antd';
import PropTypes from 'prop-types';

import PersonalInformationForm from './PersonalInformationForm';
import BusinessInformationForm from './BusinessInformationForm';
import SearchPerson from '../SearchPerson';
import {accountCreationSelectMemberError} from '../../helpers/text';

const AccountCreateInformation = ({
                                        isBusiness,
                                        firstName,
                                        setFirstName,
                                        lastName,
                                        setLastName,
                                        birthDate,
                                        setBirthDate,
                                        email,
                                        setEmail,
                                        password,
                                        setPassword,
                                        confirmPassword,
                                        setConfirmPassword,
                                        personID,
                                        setPersonID,
                                        businessName,
                                        setBusinessName,
                                        nameOnContract,
                                        setNameOnContract,
                                        dbaName,
                                        setDBAName,
                                        websiteAddress,
                                        setWebsiteAddress,
                                        taxRegistrationNumber,
                                        setTaxRegistrationNumber,
                                        businessRegistrationNumber,
                                        setBusinessRegistrationNumber,
                                        industryCode,
                                        setIndustryCode,
                                        companySize,
                                        setCompanySize,
                                        handleFinish
                                    }) => {
    const [form] = Form.useForm();

    // If a user click back on review page, redirect to the tab they were on. Password will be null unless they filled.
    const [activeTabKey, setActiveTabKey] = useState(password ? 'fill' : 'search');
    const [disableFinish, setDisableFinish] = useState(false);

    // Disable next if a user tries to proceed without a radio button selected on search person component.
    const onFinish = () => {
        if (!personID && !firstName){
            setDisableFinish(true);
        } else {
            // If the user proceeds on search page after filling form data, set passwords to null (unneeded).
            if (personID !== null) {
                setPassword(null);
                setConfirmPassword(null);
            }

            handleFinish();
        }
    }

    // Tabs are Select an existing member / Create a new member.
    const onTabChange = (key) => {
        setActiveTabKey(key);
        setDisableFinish(false);

        // There can be a lot of finicky behaviour if a user is clicking/typing around chaotically.
        // If a user changes tabs when they have already selected a pre-existing person.
        // Clear person_id and populated fields.
        if (personID !== null) {
            form.resetFields();
            setPersonID(null);
            setFirstName(null);
            setLastName(null);
            setBirthDate(null);
            setEmail(null);
            setPassword(null);
            setConfirmPassword(null);
        }
    };

    // Person states are referring to the filled fields found on the form/review page.
    // We pass these into the SearchPerson component so it can populate them from the table data.
    const personStates = {
        person_id: personID,
        set_person_id: setPersonID,
        set_first_name: setFirstName,
        set_last_name: setLastName,
        set_user_email: setEmail,
        set_birth_date: setBirthDate,
    }

    // The following components are put into JSX elements to organize them neatly amongst the tab functionality.
    // Sometimes the form element is adjacent and sometimes wrapped. SearchPerson has a form so it cannot be nested.
    const searchPersonJSX =
        <SearchPerson
            key='search'
            personStates={personStates}
        />

    const personalInfoJSX =
        <Form
            key='personal_form'
            form={form}
            onFinish={onFinish}
            id="account-create-information"
            name="account-create-information"
            className="account-create-information"
            layout="vertical"
        >
            <PersonalInformationForm
                key={'personal'}
                isBusiness={isBusiness}
                firstName={firstName}
                setFirstName={setFirstName}
                lastName={lastName}
                setLastName={setLastName}
                birthDate={birthDate}
                setBirthDate={setBirthDate}
                email={email}
                setEmail={setEmail}
                passwordRequired={true}
                password={password}
                setPassword={setPassword}
                confirmPassword={confirmPassword}
                setConfirmPassword={setConfirmPassword}
            />
        </Form>

    const businessInfoJSX =
        <Form
            key='business_form'
            form={form}
            onFinish={onFinish}
            id="account-create-information"
            name="account-create-information"
            className="account-create-information"
            layout="vertical"
        >
            <BusinessInformationForm
                key='business'
                businessName={businessName}
                setBusinessName={setBusinessName}
                nameOnContract={nameOnContract}
                setNameOnContract={setNameOnContract}
                dbaName={dbaName}
                setDBAName={setDBAName}
                websiteAddress={websiteAddress}
                setWebsiteAddress={setWebsiteAddress}
                taxRegistrationNumber={taxRegistrationNumber}
                setTaxRegistrationNumber={setTaxRegistrationNumber}
                businessRegistrationNumber={businessRegistrationNumber}
                setBusinessRegistrationNumber={setBusinessRegistrationNumber}
                industryCode={industryCode}
                setIndustryCode={setIndustryCode}
                companySize={companySize}
                setCompanySize={setCompanySize}
            />
        </Form>

    const formJSX =
        <Form
            key='form'
            form={form}
            onFinish={onFinish}
            id="account-create-information"
            name="account-create-information"
            className="account-create-information"
            layout="vertical"
        />

    /**
     *  Four different renders of this page can occur. (Search = search person component / Fill = forms).
     *
     *  1) Search Personal
     *  2) Fill Personal
     *  3) Search personal, business fill
     *  4) Fill personal, business fill
    */

    let contentList;

    if (isBusiness === true) {
        contentList = {
            search: [searchPersonJSX, businessInfoJSX],
            fill: [personalInfoJSX, businessInfoJSX]
        };
    } else {
        contentList = {
            search: [searchPersonJSX, formJSX],
            fill: [personalInfoJSX]
        };
    }

    const tabList = [
        {key: 'search', tab: 'Select an existing person'},
        {key: 'fill', tab: 'Create a new person'}
    ];

    return (
        <>
            <Card
                title={
                    <span className="account-create-information-title">
                        {isBusiness ? "Business Account" : "Personal Account"}
                    </span>
                }
                headStyle={{backgroundColor: '#F5F5F5'}}
                bordered={false}
                className="account-create-information-card"
                tabList={tabList}
                activeTabKey={activeTabKey}
                onTabChange={(key) => {
                    onTabChange(key);
                }}
            >
                {contentList[activeTabKey]}
            </Card>
            <br/>
            {disableFinish ? accountCreationSelectMemberError : null}
        </>
    );
};

AccountCreateInformation.propTypes = {
    isBusiness: PropTypes.bool.isRequired,
    firstName: PropTypes.string,
    setFirstName: PropTypes.func.isRequired,
    lastName: PropTypes.string,
    setLastName: PropTypes.func.isRequired,
    birthDate: PropTypes.object,
    setBirthDate: PropTypes.func.isRequired,
    email: PropTypes.string,
    setEmail: PropTypes.func.isRequired,
    password: PropTypes.string,
    setPassword: PropTypes.func.isRequired,
    confirmPassword: PropTypes.string,
    setConfirmPassword: PropTypes.func.isRequired,
    businessName: PropTypes.string,
    setBusinessName: PropTypes.func.isRequired,
    nameOnContract: PropTypes.string,
    setNameOnContract: PropTypes.func.isRequired,
    dbaName: PropTypes.string,
    setDBAName: PropTypes.func.isRequired,
    websiteAddress: PropTypes.string,
    setWebsiteAddress: PropTypes.func.isRequired,
    taxRegistrationNumber: PropTypes.string,
    setTaxRegistrationNumber: PropTypes.func.isRequired,
    businessRegistrationNumber: PropTypes.string,
    setBusinessRegistrationNumber: PropTypes.func.isRequired,
    industryCode: PropTypes.string,
    setIndustryCode: PropTypes.func.isRequired,
    companySize: PropTypes.string,
    setCompanySize: PropTypes.func.isRequired,
    handleFinish: PropTypes.func.isRequired,
};

export default AccountCreateInformation;